// Atoms del detalle: grid label/valor en 2 columnas con placeholder de valor
// vacío consistente ('—' en tercer nivel, nunca '-'). Misma familia en los
// overlays de detalle de facturas, pagos, servicios y excepciones.
import type { ReactNode } from 'react';
import { Typography, theme } from 'antd';

const { Text } = Typography;

export function ValorVacio() {
  const { token } = theme.useToken();
  return <span style={{ color: token.colorTextTertiary }}>—</span>;
}

export interface CampoInfoProps {
  label: string;
  value?: ReactNode;
  /** Span both grid columns (long text like comments). */
  full?: boolean;
}

export function CampoInfo({ label, value, full = false }: CampoInfoProps) {
  const { token } = theme.useToken();
  return (
    <div style={{ minWidth: 0, gridColumn: full ? '1 / -1' : undefined }}>
      <Text
        type="secondary"
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.4,
          marginBottom: 4,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          display: 'block',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.5,
          color: token.colorText,
          overflowWrap: 'break-word',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value ?? <ValorVacio />}
      </Text>
    </div>
  );
}

export function MontoDestacado({ value, color }: { value: ReactNode; color?: string }) {
  const { token } = theme.useToken();
  return (
    <span
      style={{
        fontSize: 18,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: color ?? token.colorText,
      }}
    >
      {value}
    </span>
  );
}

/**
 * Bloque compacto de datos faltantes: lista en una sola línea (terciario) qué
 * campos opcionales no tienen valor registrado. Mantiene visible la
 * incompletitud de la ficha sin ocupar filas completas del grid (H8:
 * progressive disclosure). No renderiza nada si no hay faltantes.
 */
export function InfoFaltante({ intro, labels }: { intro: string; labels: string[] }) {
  const { token } = theme.useToken();
  if (labels.length === 0) return null;
  return (
    <Text
      type="secondary"
      style={{
        display: 'block',
        marginTop: 16,
        fontSize: 12,
        lineHeight: 1.5,
        color: token.colorTextTertiary,
      }}
    >
      {intro}: <span style={{ color: token.colorTextTertiary }}>{labels.join(' · ')}</span>
    </Text>
  );
}

export function CamposInfoGrid({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        columnGap: 32,
        rowGap: 18,
      }}
    >
      {children}
    </div>
  );
}
